import React, { useEffect, useState } from 'react';
import {
  Building,
  Car,
  LineChart,
  Handshake,
  GlobeLock,
  Boxes,
  ShieldCheck,
  Earth,
  Banknote,
  ChartLine,
  ChartPie,
  Settings2,
  Download,
} from 'lucide-react';
import { cn } from '../../../../../utils/classnames';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import RealEstateTab from './propertiesTabs/RealEstateTab';
import VehicleTab from './propertiesTabs/VehicleTab';
import FinancialTab from './propertiesTabs/FinancialTab';
import SocietaryTab from './propertiesTabs/SocietaryTab';
import CryptoTab from './propertiesTabs/CryptoTab';
import OtherTab from './propertiesTabs/OtherTab';
import InsuranceTab from './propertiesTabs/InsuranceTab';
import ForeignTab from './propertiesTabs/ForeignTab';
import RetirementTab from './propertiesTabs/RetirementTab';
import CustomSelect from '../../../../../components/ui/selects/CustomSelect';
import InvestmentPolicy from './investmentStrategy/InvestmentPolicy';
import { useToast } from '../../../../../components/ui/toast/ToastContext';
import InvestmentAllocations from './investmentStrategy/InvestmentAllocations';
import CustomizeAllocations from './investmentStrategy/CustomizeAllocations';
import ExportReports from './investmentStrategy/ExportReports';

const InvestmentStrategyTab = ({ clientId, scope, onSuccess, canWrite }) => {
  const [activeTab, setActiveTab] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('subTab') || 'investment_policy';
  });

  const [hoveredTab, setHoveredTab] = useState(null);
  const [data, setData] = useState({});
  const [walletForm, setWalletForm] = useState({
    walletProfileId: null,
  });
  const [walletProfileOptions, setWalletProfileOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const { addToast } = useToast();

  const firstTabs = [
    {
      key: 'investment_policy',
      label: 'Estratégia de Investimentos',
      icon: ChartLine,
    },
    {
      key: 'investment_allocation',
      label: 'Alocação Patrimonial',
      icon: ChartPie,
    },
  ];

  const secondTabs = [
    {
      key: 'customize_allocations',
      label: 'Personalizar Alocações',
      icon: Settings2,
    },
    {
      key: 'export_reports',
      label: 'Exportar Dados',
      icon: Download,
    },
  ];

  const handleTabChange = (key) => {
    setActiveTab(key);

    const url = new URL(window.location);
    url.searchParams.set('subTab', key);
    window.history.pushState({}, '', url);
  };

  useEffect(() => {
    fetchData();
  }, [clientId, scope]);

  const fetchData = () => {
    fetch(`/app/planner/client/${clientId}/dashboard/investment_strategy?scope=${scope}`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);

        setData(camelData);

        console.log('Fetched investment strategy data:', camelData);

        setWalletForm((prev) => ({
          ...prev,
          walletProfileId: camelData.walletProfile?.id || null,
        }));

        setWalletProfileOptions(camelData.walletProfileOptions || []);
      });
  };

  const handleWalletChange = async (e) => {
    const newWalletProfileId = e?.target?.value ?? e;

    const previousWalletProfileId = walletForm.walletProfileId;

    setWalletForm((prev) => ({
      ...prev,
      walletProfileId: newWalletProfileId,
    }));

    setLoading(true);

    try {
      const response = await fetch(`/app/planner/client/${clientId}/dashboard/investment_strategy`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          client: {
            wallet_profile_id: newWalletProfileId,
          },
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Erro ao atualizar perfil de carteira');
      }

      await fetchData();
      addToast({ type: 'success', message: 'Perfil de carteira do cliente atualizado com sucesso' });

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Erro ao atualizar perfil de carteira:', error);
      addToast({ type: 'error', message: error.message || 'Erro ao atualizar perfil de carteira' });

      setWalletForm((prev) => ({
        ...prev,
        walletProfileId: previousWalletProfileId,
      }));
    } finally {
      setLoading(false);
    }
  };

  const renderTabs = (tabs) => (
    <ul className="flex flex-col">
      {tabs.map((tab, index) => {
        const isActive = activeTab === tab.key;
        const isHovered = hoveredTab === tab.key;

        const isCurrentActiveOrHovered = isActive || isHovered;
        const isPrevActiveOrHovered =
          index > 0 && (activeTab === tabs[index - 1].key || hoveredTab === tabs[index - 1].key);

        const shouldRenderHr = index > 0;
        const hrColor = isCurrentActiveOrHovered || isPrevActiveOrHovered ? 'bg-white' : 'bg-blueWhisper';

        return (
          <React.Fragment key={tab.key}>
            {shouldRenderHr && <hr className={cn('mx-5 h-px border-0', hrColor)} />}

            <li
              className={cn(isActive ? 'bg-blueExtraUltraLight' : 'hover:bg-gray-100')}
              onClick={() => handleTabChange(tab.key)}
              onMouseEnter={() => setHoveredTab(tab.key)}
              onMouseLeave={() => setHoveredTab(null)}
            >
              <div className="flex items-center gap-3 px-5 pt-5 py-4 cursor-pointer text-base font-medium text-blueDarker">
                {tab.icon &&
                  React.createElement(tab.icon, {
                    className: 'text-tealMedium',
                    size: 18,
                    strokeWidth: 2,
                  })}

                <span>{tab.label}</span>
              </div>
            </li>
          </React.Fragment>
        );
      })}
    </ul>
  );

  return (
    <div
      className="grid gap-5"
      style={{
        gridTemplateColumns: '320px minmax(0, 1fr)',
      }}
    >
      <div className="flex flex-col gap-5">
        {/* Primeira seção de tabs */}
        <div className="py-5 rounded-custom20 bg-white flex flex-col gap-4">
          <span className="text-sm font-semibold text-blueSteel px-5 flex justify-center">ESTRATÉGIAS</span>
          {renderTabs(firstTabs)}
        </div>

        {/* Seção de carteira */}
        <div className="bg-white rounded-custom20 p-5 flex flex-col gap-4">
          <span className="text-sm font-semibold text-blueSteel px-5 flex justify-center">PERFIL DE CARTEIRA</span>
          {canWrite ? (
            <CustomSelect
              name="walletProfileId"
              py="py-3"
              options={walletProfileOptions.map((profile) => ({
                label: profile.label,
                value: profile.value,
                colorHex: profile.colorHex,
              }))}
              value={walletForm.walletProfileId}
              onChange={handleWalletChange}
              disabled={walletProfileOptions.length === 0 || loading}
            />
          ) : (
            <div className="mx-5 py-3 px-3 rounded-lg bg-blueExtraUltraLight text-blueDarker text-sm text-center">
              {walletProfileOptions.find((profile) => profile.value === walletForm.walletProfileId)?.label || '—'}
            </div>
          )}
        </div>

        {/* Segunda seção de tabs */}
        <div className="py-5 rounded-custom20 bg-white flex flex-col gap-4">
          <span className="text-sm font-semibold text-blueSteel px-5 flex justify-center">FERRAMENTAS</span>
          {renderTabs(secondTabs)}
        </div>
      </div>

      {/* Content */}
      <div className="min-w-0">
        {activeTab === 'investment_policy' && (
          <InvestmentPolicy clientId={clientId} scope={scope} canWrite={canWrite} />
        )}
        {activeTab === 'investment_allocation' && (
          <InvestmentAllocations clientId={clientId} scope={scope} canWrite={canWrite} />
        )}
        {activeTab === 'customize_allocations' && (
          <CustomizeAllocations clientId={clientId} scope={scope} canWrite={canWrite} />
        )}
        {activeTab === 'export_reports' && <ExportReports clientId={clientId} scope={scope} canWrite={canWrite} />}
      </div>
    </div>
  );
};

export default InvestmentStrategyTab;
